'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/photos-documents.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/photos-documents#field-photos)
 */

/**
 * FIELD SERVICE PHOTOS TAB
 * 
 * View and upload photos for field service jobs (admin / back-office version).
 * Displays photos organized by type, matching PhotoCapture.tsx types used by field workers.
 * Supports drag-drop upload from desktop.
 * 
 * Data Source: work_item_photos table (via /api/v1/work-items/media/upload)
 * Photo types: before, after, during, proof, odometer, inspection, general
 *   — must match SmartTask V2 PhotoCapture component exactly
 * 
 * Updated: February 2026 — aligned with PhotoCapture types and cloud media API
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Camera, MapPin, Clock, User, X, Download, ZoomIn, ZoomOut, Upload, Plus, Edit2, Check, Loader2, AlertTriangle, ShieldCheck, Trash2, RotateCcw, ChevronDown, ChevronUp, MoreHorizontal, Lock, Globe, FileText } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';
import { getTimePartsSAST, getDateStrSAST } from '@/lib/sastTime';
import { compressImage } from '@/lib/compressImage';
import AppointmentReferenceBadge from '@/components/work-items/AppointmentReferenceBadge';
import AppointmentCompletionRequirementOverrideCard from '@/components/work-items/AppointmentCompletionRequirementOverrideCard';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import UserDocsLink from '@/components/shared/UserDocsLink';
import type {
  AppointmentCompletionRequirementKey,
  AppointmentTypeAwareWorkItem,
  ConfigurableTypeOption,
} from '@/lib/work-items/appointmentTypeConfig';
import {
  ALL_PHOTO_VISIBILITY_VALUES,
  DEFAULT_PHOTO_VISIBILITY,
  PHOTO_VISIBILITY_LABELS,
  type PhotoVisibility,
} from '@/lib/work-items/photoVisibility';

// ============================================================================
// TYPES — must mirror SmartTask V2 JobPhoto['type']
// ============================================================================

type PhotoType = 'before' | 'after' | 'during' | 'proof' | 'odometer' | 'inspection' | 'general';
type UploadPhotoType = 'before' | 'during' | 'after';
interface Photo {
  id: string;
  url: string;
  type: PhotoType;
  caption: string | null;
  location: { latitude: number; longitude: number; accuracy: number | null } | null;
  taken_at: string;
  original_taken_at?: string | null;
  updated_at?: string | null;
  is_edited?: boolean;
  taken_by: string;
  captured_by_user_id?: number | null;
  work_item_id: string;
  is_from_related?: boolean;
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
  visibility?: PhotoVisibility;
  ai_analysis?: { flagged?: boolean; description?: string; relevance?: string; flag_reason?: string } | null;
}

/** Shape returned by `/api/v1/work-items/media/upload` list endpoint (partial). */
interface WorkItemPhotoApiRow {
  id?: string | number;
  url?: string;
  type?: string;
  caption?: string | null;
  location?: Photo['location'];
  taken_at?: string;
  original_taken_at?: string | null;
  updated_at?: string | null;
  is_edited?: boolean;
  taken_by?: string | null;
  captured_by_user_id?: number | null;
  work_item_id?: string;
  is_from_related?: boolean;
  source_appointment_id?: string | null;
  source_appointment_number?: string | null;
  visibility?: string;
}

interface FieldServicePhotosTabProps {
  workItemId: string;
  isActive: boolean;
  /** When true, fetch photos from related appointments too (used on Job view) */
  includeRelated?: boolean;
  appointmentWorkItem?: AppointmentTypeAwareWorkItem | null;
  appointmentTypeOptions?: ConfigurableTypeOption[];
  onAppointmentRequirementOverrideChange?: (
    requirementKey: AppointmentCompletionRequirementKey,
    updates: { enabled?: boolean; reason?: string }
  ) => void;
  /** When false, uploads, deletes, and caption edits are disabled (operations team RBAC). Defaults to true. */
  allowPhotoEdits?: boolean;
  /**
   * `edit_photos` action permission (Actions tab matrix). When false, caption,
   * captured-time, and visibility edits are only allowed on the user's OWN
   * photos — editing other users' photos is denied. Defaults to true.
   */
  allowPhotoDetailEdits?: boolean;
  /** Current user id — used to allow detail edits on the user's own photos when `allowPhotoDetailEdits` is false. */
  currentUserId?: string | number | null;
  /** When true, all photo editing (including visibility) is disabled — used when the job/appointment is closed or the modal is in view mode. Defaults to false. */
  photosLocked?: boolean;
}

// ============================================================================
// PHOTO TYPE CONFIG — matches PhotoCapture.tsx PHOTO_TYPE_LABELS
// ============================================================================

const PHOTO_TYPE_CONFIG: Record<PhotoType, { label: string; emoji: string; badgeLight: string; badgeDark: string; filterActive: string }> = {
  before: { label: 'Before', emoji: '📸', badgeLight: 'bg-blue-100 text-blue-700 border-blue-300', badgeDark: 'bg-blue-900/60 text-blue-300 border-blue-700', filterActive: 'bg-blue-600 text-white' },
  after: { label: 'After', emoji: '✅', badgeLight: 'bg-green-100 text-green-700 border-green-300', badgeDark: 'bg-green-900/60 text-green-300 border-green-700', filterActive: 'bg-green-600 text-white' },
  during: { label: 'During Work', emoji: '🔧', badgeLight: 'bg-amber-100 text-amber-700 border-amber-300', badgeDark: 'bg-amber-900/60 text-amber-300 border-amber-700', filterActive: 'bg-amber-600 text-white' },
  proof: { label: 'Proof', emoji: '🔍', badgeLight: 'bg-purple-100 text-purple-700 border-purple-300', badgeDark: 'bg-purple-900/60 text-purple-300 border-purple-700', filterActive: 'bg-purple-600 text-white' },
  odometer: { label: 'Odometer', emoji: '🚗', badgeLight: 'bg-cyan-100 text-cyan-700 border-cyan-300', badgeDark: 'bg-cyan-900/60 text-cyan-300 border-cyan-700', filterActive: 'bg-cyan-600 text-white' },
  inspection: { label: 'Inspection', emoji: '🔎', badgeLight: 'bg-orange-100 text-orange-700 border-orange-300', badgeDark: 'bg-orange-900/60 text-orange-300 border-orange-700', filterActive: 'bg-orange-600 text-white' },
  general: { label: 'General', emoji: '📷', badgeLight: 'bg-gray-100 text-gray-700 border-gray-300', badgeDark: 'bg-gray-700/60 text-gray-300 border-gray-600', filterActive: 'bg-gray-600 text-white' },
};

const ALL_PHOTO_TYPES: PhotoType[] = ['before', 'after', 'during', 'proof', 'odometer', 'inspection', 'general'];
const FIELD_SERVICE_UPLOAD_TYPES: UploadPhotoType[] = ['before', 'during', 'after'];

// ============================================================================
// HELPERS
// ============================================================================

const getAuthToken = (): string | null => authService.getTokens()?.access_token || null;

const SOFT_DELETE_MARKER = '[SOFT_DELETED]';

const PHOTO_VISIBILITY_CONFIG: Record<PhotoVisibility, { label: string; icon: typeof Lock; badgeLight: string; badgeDark: string }> = {
  customer: { label: PHOTO_VISIBILITY_LABELS.customer, icon: Globe, badgeLight: 'bg-emerald-100 text-emerald-700', badgeDark: 'bg-emerald-900/40 text-emerald-300' },
  internal: { label: PHOTO_VISIBILITY_LABELS.internal, icon: Lock, badgeLight: 'bg-yellow-100 text-yellow-700', badgeDark: 'bg-yellow-900/40 text-yellow-300' },
  closing_details: { label: PHOTO_VISIBILITY_LABELS.closing_details, icon: FileText, badgeLight: 'bg-blue-100 text-blue-700', badgeDark: 'bg-blue-900/40 text-blue-300' },
};

const getVisibilityConfig = (visibility: string | undefined | null) =>
  PHOTO_VISIBILITY_CONFIG[visibility as PhotoVisibility] || PHOTO_VISIBILITY_CONFIG[DEFAULT_PHOTO_VISIBILITY];

const formatDateTime = (dateString: string): string =>
  new Date(dateString).toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const toDateTimeLocalValue = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = getDateStrSAST(date);
  const timeParts = getTimePartsSAST(date);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${dateStr}T${pad(timeParts.hours)}:${pad(timeParts.minutes)}`;
};

const toIsoDateTime = (value: string): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const getTypeConfig = (type: string) => PHOTO_TYPE_CONFIG[type as PhotoType] || PHOTO_TYPE_CONFIG.general;

const isSoftDeletedPhoto = (caption: string | null | undefined): boolean =>
  typeof caption === 'string' && caption.trimStart().startsWith(SOFT_DELETE_MARKER);

const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
  reader.readAsDataURL(file);
});

const dataUrlToBlob = (dataUrl: string, fallbackMimeType = 'application/octet-stream'): Blob => {
  const match = dataUrl.match(/^data:([^;,]+)?(;base64)?,([\s\S]*)$/i);
  if (!match) {
    throw new Error('Compressed image is not a valid data URL');
  }

  const mimeType = match[1] || fallbackMimeType;
  const isBase64 = Boolean(match[2]);
  const payload = match[3] || '';

  if (!payload) {
    return new Blob([], { type: mimeType });
  }

  if (isBase64) {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return new Blob([bytes], { type: mimeType });
  }

  return new Blob([decodeURIComponent(payload)], { type: mimeType });
};

const dataUrlToFile = (dataUrl: string, originalFile: File): File => {
  const blob = dataUrlToBlob(dataUrl, originalFile.type || 'image/jpeg');
  const fileExtension = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
  const baseName = originalFile.name.replace(/\.[^.]+$/, '');
  const nextFileName = `${baseName}.${fileExtension}`;

  return new File([blob], nextFileName, {
    type: blob.type || originalFile.type || 'image/jpeg',
    lastModified: originalFile.lastModified,
  });
};

const compressUploadImage = async (file: File): Promise<File> => {
  const dataUrl = await fileToDataUrl(file);
  const compressedDataUrl = await compressImage(dataUrl, 1280, 0.65);
  return dataUrlToFile(compressedDataUrl, file);
};

// ============================================================================
// UPLOAD TYPE SELECTOR (module-level for stable identity)
// ============================================================================

interface UploadTypeSelectProps {
  value: UploadPhotoType;
  onChange: (value: UploadPhotoType) => void;
  disabled: boolean;
  isDark: boolean;
  className?: string;
}

function UploadTypeSelect({ value, onChange, disabled, isDark, className = '' }: UploadTypeSelectProps) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as UploadPhotoType)}
      disabled={disabled}
      className={`px-3 py-2 border rounded-lg text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} ${className}`}
    >
      {FIELD_SERVICE_UPLOAD_TYPES.map(t => {
        const cfg = PHOTO_TYPE_CONFIG[t];
        return <option key={t} value={t}>{cfg.emoji} {cfg.label}</option>;
      })}
    </select>
  );
}

// ============================================================================
// LIGHTBOX MODAL COMPONENT
// ============================================================================

interface LightboxProps {
  photo: Photo;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onCaptionSave?: (photoId: string, caption: string) => Promise<boolean>;
  onCaptionDelete?: (photoId: string) => Promise<boolean>;
  onVisibilityChange?: (photoId: string, visibility: PhotoVisibility) => Promise<boolean>;
  onTakenAtSave?: (photoId: string, takenAt: string) => Promise<boolean>;
}

function Lightbox({ photo, onClose, onNext, onPrevious, onCaptionSave, onCaptionDelete, onVisibilityChange, onTakenAtSave }: LightboxProps) {
  const cfg = getTypeConfig(photo.type);
  const visCfg = getVisibilityConfig(photo.visibility);
  const [isEditingCaption, setIsEditingCaption] = useState(false);
  const [captionValue, setCaptionValue] = useState(photo.caption || '');
  const [savingCaption, setSavingCaption] = useState(false);
  const [captionExpanded, setCaptionExpanded] = useState(false);
  const [visibilityMenuOpen, setVisibilityMenuOpen] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [isEditingTakenAt, setIsEditingTakenAt] = useState(false);
  const [takenAtValue, setTakenAtValue] = useState('');
  const [savingTakenAt, setSavingTakenAt] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset draft caption when navigating between photos (remount would flicker the lightbox chrome).
  /* eslint-disable react-hooks/set-state-in-effect -- syncing local editor state to the opened photo record */
  useEffect(() => {
    setCaptionValue(photo.caption || '');
    setIsEditingCaption(false);
    setCaptionExpanded(false);
    setVisibilityMenuOpen(false);
    setIsEditingTakenAt(false);
    setTakenAtValue(photo.taken_at ? toDateTimeLocalValue(photo.taken_at) : '');
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [photo.id, photo.caption, photo.taken_at]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleVisibilityChange = async (visibility: PhotoVisibility) => {
    if (!onVisibilityChange) return;
    setSavingVisibility(true);
    setVisibilityMenuOpen(false);
    await onVisibilityChange(photo.id, visibility);
    setSavingVisibility(false);
  };

  const handleSaveCaption = async () => {
    if (onCaptionSave) {
      setSavingCaption(true);
      const success = await onCaptionSave(photo.id, captionValue);
      setSavingCaption(false);
      if (success) setIsEditingCaption(false);
    }
  };

  const handleSaveTakenAt = async () => {
    if (!onTakenAtSave) return;
    const isoValue = toIsoDateTime(takenAtValue);
    if (!isoValue) {
      notify.error('Please enter a valid date and time');
      return;
    }
    setSavingTakenAt(true);
    const success = await onTakenAtSave(photo.id, isoValue);
    setSavingTakenAt(false);
    if (success) setIsEditingTakenAt(false);
  };

  const handleDeleteCaption = async () => {
    if (!onCaptionDelete) return;
    await confirm({
      title: 'Delete Description',
      message: 'Remove the description for this photo? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        setSavingCaption(true);
        const success = await onCaptionDelete(photo.id);
        setSavingCaption(false);
        if (success) {
          setCaptionValue('');
          setIsEditingCaption(false);
        }
      }
    });
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 4));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 1));
  const handleResetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'ArrowLeft' && onPrevious) onPrevious();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-' || e.key === '_') handleZoomOut();
      if (e.key === '0') handleResetZoom();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrevious]);

  const imageMaxHeight = captionExpanded ? '50vh' : '78vh';

  // Rendered in a portal so the viewer escapes the WorkItemDetailModal
  // view-mode lock (`[&_button]:pointer-events-none`) that otherwise makes
  // every button in here — including Close — unclickable on locked items.
  // zIndex must sit above the modal's containerZIndex (max 200) and below
  // confirm dialogs (z-240).
  const lightboxContent = (
    <div
      className="fixed inset-0 bg-black/95 flex flex-col z-[210]"
      style={{ pointerEvents: 'auto' }}
    >
      {/* Top bar */}
      <div className="shrink-0 flex items-center justify-between p-4 z-20">
        {/* Zoom Controls */}
        <div className="flex items-center gap-2">
          <button onClick={handleZoomOut} disabled={zoom <= 1} className="p-2.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors shadow-lg border border-gray-600" title="Zoom Out (-)">
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="px-3 py-1 bg-gray-800 text-white text-sm font-mono rounded-lg border border-gray-600 min-w-[3rem] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button onClick={handleZoomIn} disabled={zoom >= 4} className="p-2.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors shadow-lg border border-gray-600" title="Zoom In (+)">
            <ZoomIn className="w-5 h-5" />
          </button>
          <button onClick={handleResetZoom} disabled={zoom === 1} className="p-2.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-full text-white transition-colors shadow-lg border border-gray-600" title="Reset Zoom (0)">
            <RotateCcw className="w-5 h-5" />
          </button>
        </div>

        {/* Close */}
        <button onClick={onClose} className="p-3 bg-red-600 hover:bg-red-700 rounded-full text-white transition-colors shadow-lg" title="Close (Esc)">
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Main content area — clicking the empty space around the photo closes */}
      <div
        className="flex-1 flex items-center justify-center min-h-0 px-4 pb-4 relative"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Nav Left */}
        {onPrevious && (
          <button onClick={onPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors shadow-lg border border-gray-600 z-10" title="Previous (←)">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
        )}

        {/* Nav Right */}
        {onNext && (
          <button onClick={onNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-gray-800 hover:bg-gray-700 rounded-full text-white transition-colors shadow-lg border border-gray-600 z-10" title="Next (→)">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        )}

        {/* Image & caption */}
        <div className="flex flex-col items-center max-w-[95vw] max-h-full">
          <div
            ref={containerRef}
            className="relative overflow-hidden rounded-lg shadow-2xl cursor-grab active:cursor-grabbing bg-black"
            style={{ maxHeight: imageMaxHeight, maxWidth: '100%', transition: 'max-height 0.2s ease' }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              ref={imageRef}
              src={photo.url}
              alt={`${cfg.label} photo`}
              className="max-w-full object-contain transition-transform duration-75 ease-out"
              style={{
                maxHeight: imageMaxHeight,
                transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                cursor: zoom > 1 ? 'grab' : 'default',
                transition: 'max-height 0.2s ease',
              }}
              draggable={false}
            />
          </div>

          {/* Caption panel — collapsible and compact */}
          <div className={`mt-3 bg-gray-900 border border-gray-700 rounded-xl text-white shadow-2xl transition-all duration-200 overflow-hidden ${captionExpanded ? 'p-4 w-full max-w-2xl' : 'py-2 px-3 w-auto max-w-2xl'}`}>
            {captionExpanded ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-700">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-2xl">{cfg.emoji}</span>
                    <div className="min-w-0">
                      <span className="font-bold block truncate">{cfg.label} Photo</span>
                      {/* Visibility toggle */}
                      {onVisibilityChange && (
                        <div className="relative mt-1">
                          <button
                            onClick={() => setVisibilityMenuOpen(prev => !prev)}
                            disabled={savingVisibility}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-colors ${visCfg.badgeDark} hover:opacity-80 disabled:opacity-50`}
                            title="Change photo visibility"
                          >
                            {savingVisibility ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              React.createElement(visCfg.icon, { className: 'w-3 h-3' })
                            )}
                            {visCfg.label}
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          {visibilityMenuOpen && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setVisibilityMenuOpen(false)} />
                              <div className="absolute left-0 top-full mt-1 z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[160px]">
                                {ALL_PHOTO_VISIBILITY_VALUES.map((v) => {
                                  const vCfg = PHOTO_VISIBILITY_CONFIG[v];
                                  const isActive = (photo.visibility || 'customer') === v;
                                  return (
                                    <button
                                      key={v}
                                      onClick={() => handleVisibilityChange(v)}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors text-left ${isActive ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700/50'}`}
                                    >
                                      {React.createElement(vCfg.icon, { className: 'w-3.5 h-3.5' })}
                                      {vCfg.label}
                                      {isActive && <Check className="w-3.5 h-3.5 ml-auto text-green-400" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {photo.source_appointment_number ? (
                        <AppointmentReferenceBadge
                          appointmentId={photo.source_appointment_id}
                          appointmentNumber={photo.source_appointment_number}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] bg-indigo-600/30 text-indigo-300 hover:bg-indigo-600/40 transition-colors"
                          iconClassName="w-3 h-3"
                          title={`Open appointment ${photo.source_appointment_number}`}
                        >
                          <>
                            <span>📋 Appt #{photo.source_appointment_number}</span>
                          </>
                        </AppointmentReferenceBadge>
                      ) : photo.is_from_related ? (
                        <span className="text-[10px] bg-yellow-600/30 text-yellow-300 px-2 py-0.5 rounded-full">From Appointment</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a href={photo.url} download={`photo_${photo.id}.jpg`} className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors" title="Download">
                      <Download className="w-4 h-4" />
                    </a>
                    <button onClick={() => setCaptionExpanded(false)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors" title="Hide details">
                      <ChevronDown className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Caption / Description */}
                <div className="bg-gray-700/60 border border-gray-500 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-white flex items-center gap-2">
                      <Edit2 className="w-4 h-4 text-blue-400" /> Photo Description
                    </label>
                    {!isEditingCaption && onCaptionSave && (
                      <div className="flex items-center gap-1">
                        {photo.caption && onCaptionDelete && (
                          <button onClick={handleDeleteCaption} disabled={savingCaption} className="p-1.5 bg-red-600/80 hover:bg-red-600 rounded-md transition-colors text-white disabled:opacity-50" title="Delete description">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => setIsEditingCaption(true)} className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-md text-xs font-semibold transition-colors text-white shadow-sm">
                          {photo.caption ? 'Edit' : '+ Add Description'}
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditingCaption ? (
                    <div className="space-y-2">
                      <textarea
                        value={captionValue}
                        onChange={(e) => setCaptionValue(e.target.value)}
                        placeholder="Describe what this photo shows..."
                        className="w-full bg-gray-800 border border-gray-500 rounded-lg px-3 py-2 text-white placeholder-gray-400 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => setIsEditingCaption(false)} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-sm font-medium transition-colors text-gray-200">Cancel</button>
                        <button onClick={handleSaveCaption} disabled={savingCaption} className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 text-white">
                          <Check className="w-4 h-4" /> {savingCaption ? 'Saving...' : 'Save Description'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className={`text-sm leading-relaxed ${photo.caption ? 'text-gray-100' : 'text-gray-400 italic'}`}>
                      {photo.caption || 'No description yet — tap "Add Description" above.'}
                    </p>
                  )}
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Clock className="w-3.5 h-3.5 text-gray-500" />
                    {isEditingTakenAt ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="datetime-local"
                          value={takenAtValue}
                          onChange={(e) => setTakenAtValue(e.target.value)}
                          className="bg-gray-700 border border-gray-500 rounded px-2 py-1 text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <button onClick={handleSaveTakenAt} disabled={savingTakenAt} className="p-1 bg-green-600 hover:bg-green-500 rounded text-white disabled:opacity-50" title="Save time">
                          {savingTakenAt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => { setIsEditingTakenAt(false); setTakenAtValue(photo.taken_at ? toDateTimeLocalValue(photo.taken_at) : ''); }} className="p-1 bg-gray-600 hover:bg-gray-500 rounded text-white" title="Cancel">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span>{formatDateTime(photo.taken_at)}</span>
                        {photo.is_edited && photo.updated_at && (
                          <span className="text-xs italic text-gray-500">
                            (edited {formatDateTime(photo.updated_at)})
                          </span>
                        )}
                        {onTakenAtSave && (
                          <button onClick={() => { setTakenAtValue(photo.taken_at ? toDateTimeLocalValue(photo.taken_at) : ''); setIsEditingTakenAt(true); }} className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors" title="Edit time">
                            <Edit2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {photo.taken_by && (
                    <div className="flex items-center gap-2 text-gray-300">
                      <User className="w-3.5 h-3.5 text-gray-500" />
                      <span className="truncate">{photo.taken_by}</span>
                    </div>
                  )}
                  {photo.location && (
                    <div className="flex items-center gap-2 text-gray-300 col-span-2">
                      <MapPin className="w-3.5 h-3.5 text-green-500" />
                      <a
                        href={`https://www.google.com/maps?q=${photo.location.latitude},${photo.location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 underline"
                      >
                        View on Map ({photo.location.latitude.toFixed(4)}, {photo.location.longitude.toFixed(4)})
                      </a>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Collapsed caption bar */
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xl">{cfg.emoji}</span>
                  <span className="font-semibold text-sm truncate">{cfg.label}</span>
                  <span className={`text-sm truncate max-w-[180px] sm:max-w-[260px] ${photo.caption ? 'text-gray-300' : 'text-gray-500 italic'}`}>
                    {photo.caption || 'No description'}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {photo.caption && onCaptionSave && (
                    <button onClick={() => setIsEditingCaption(true)} className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors text-white" title="Edit description">
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {photo.caption && onCaptionDelete && (
                    <button onClick={handleDeleteCaption} disabled={savingCaption} className="p-1.5 bg-red-600/80 hover:bg-red-600 rounded-md transition-colors text-white disabled:opacity-50" title="Delete description">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <a href={photo.url} download={`photo_${photo.id}.jpg`} className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-md transition-colors text-white" title="Download">
                    <Download className="w-4 h-4" />
                  </a>
                  <button onClick={() => setCaptionExpanded(true)} className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors text-white" title="Show details">
                    <ChevronUp className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return lightboxContent;
  return createPortal(lightboxContent, document.body);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FieldServicePhotosTab({
  workItemId,
  isActive,
  includeRelated = true,
  appointmentWorkItem,
  appointmentTypeOptions,
  onAppointmentRequirementOverrideChange,
  allowPhotoEdits = true,
  allowPhotoDetailEdits = true,
  currentUserId = null,
  photosLocked = false,
}: FieldServicePhotosTabProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  const canEditPhotos = allowPhotoEdits && !photosLocked;
  // Detail edits (caption, captured-time, visibility, delete) require the
  // `edit_photos` action permission — but users always retain those rights on
  // their OWN photos, mirroring own-vs-others comment semantics.
  const isOwnPhoto = useCallback((photo: Photo | undefined | null) => (
    currentUserId != null
    && photo?.captured_by_user_id != null
    && String(photo.captured_by_user_id) === String(currentUserId)
  ), [currentUserId]);
  const canEditPhotoDetailsFor = useCallback((photo: Photo | undefined | null) => (
    canEditPhotos && (allowPhotoDetailEdits || isOwnPhoto(photo))
  ), [canEditPhotos, allowPhotoDetailEdits, isOwnPhoto]);
  const notifyPhotoEditBlocked = useCallback((detailOnly = false) => {
    if (photosLocked) {
      notify.warning('Photos are read-only because this work item is closed or locked.');
    } else if (detailOnly && allowPhotoEdits) {
      notify.warning('Your team role does not include editing photo captions or visibility.');
    } else {
      notify.warning('Your team role does not include editing field service photos.');
    }
  }, [photosLocked, allowPhotoEdits]);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [uploadType, setUploadType] = useState<UploadPhotoType>('before');
  const [deletingPhotoId, setDeletingPhotoId] = useState<string | null>(null);
  const [thumbVisibilityMenuId, setThumbVisibilityMenuId] = useState<string | null>(null);
  const [thumbVisibilitySavingId, setThumbVisibilitySavingId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const includeParam = includeRelated ? '&include_related=true' : '';
      const response = await fetch(`/api/v1/work-items/media/upload?work_item_id=${workItemId}&type=photos${includeParam}`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPhotos(
            (data.photos || [])
              .map((p: WorkItemPhotoApiRow) => ({
                id: String(p.id),
                url: p.url,
                type: (typeof p.type === 'string' && ALL_PHOTO_TYPES.includes(p.type as PhotoType)
                  ? (p.type as PhotoType)
                  : 'general'),
                caption: p.caption || null,
                location: p.location || null,
                taken_at: p.taken_at,
                original_taken_at: p.original_taken_at || null,
                updated_at: p.updated_at || null,
                is_edited: p.is_edited || false,
                taken_by: p.taken_by || 'Unknown',
                captured_by_user_id: p.captured_by_user_id ?? null,
                work_item_id: p.work_item_id || workItemId,
                is_from_related: p.is_from_related || false,
                source_appointment_id: p.source_appointment_id || null,
                source_appointment_number: p.source_appointment_number || null,
                visibility: (p.visibility as PhotoVisibility) || 'customer',
              }))
              .filter((p: Photo) => !isSoftDeletedPhoto(p.caption))
          );
        }
      }
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId, includeRelated]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      void loadPhotos();
      hasLoadedRef.current = true;
    }
  }, [isActive, workItemId, loadPhotos]);

  // ── Drag & drop ──
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEditPhotos) {
      return;
    }
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!canEditPhotos) {
      notifyPhotoEditBlocked();
      return;
    }
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const imageFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      if (imageFiles.length > 0) handleUpload(imageFiles);
      else notify.warning('Please drop only image files');
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canEditPhotos) {
      notifyPhotoEditBlocked();
      return;
    }
    if (e.target.files && e.target.files.length > 0) {
      const imageFiles = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
      if (imageFiles.length > 0) handleUpload(imageFiles);
      else notify.warning('Please select only image files');
    }
  };

  // ── Upload via base64 JSON — same mechanism as SmartTask useEvidence.ts ──
  async function handleUpload(files: File[]) {
    if (!canEditPhotos) {
      notifyPhotoEditBlocked();
      return;
    }
    setUploading(true);
    const counts = { success: 0, failed: 0 };

    for (const file of files) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const compressed = await compressImage(dataUrl, 1280, 0.65);

        const res = await fetch('/api/v1/work-items/media/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            work_item_id: workItemId,
            photo_data: compressed,
            type: uploadType,
          }),
        });

        if (res.ok) counts.success++;
        else counts.failed++;
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        counts.failed++;
      }
    }

    setUploading(false);
    loadPhotos();
    if (fileInputRef.current) fileInputRef.current.value = '';

    if (counts.success > 0 && counts.failed === 0) notify.success(`${counts.success} photo${counts.success > 1 ? 's' : ''} uploaded`);
    else if (counts.success > 0 && counts.failed > 0) notify.warning(`${counts.success} uploaded, ${counts.failed} failed`);
    else if (counts.failed > 0) notify.error(`Failed to upload ${counts.failed} photo${counts.failed > 1 ? 's' : ''}`);
  }

  // ── Delete ──
  const handleDeletePhoto = async (photo: Photo) => {
    if (!canEditPhotoDetailsFor(photo)) {
      notifyPhotoEditBlocked(true);
      return;
    }
    await confirm({
      title: 'Remove Photo',
      message: `Remove this ${getTypeConfig(photo.type).label.toLowerCase()} photo? This is a soft delete and can be restored in the database if needed.`,
      confirmText: 'Remove',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setDeletingPhotoId(photo.id);
          const existingCaption = photo.caption?.trim() || '';
          const softDeletedCaption = `${SOFT_DELETE_MARKER} ${new Date().toISOString()}${existingCaption ? ` | ${existingCaption}` : ''}`;

          const res = await fetch(`/api/v1/work-items/media/upload`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              photo_id: photo.id,
              work_item_id: photo.work_item_id,
              caption: softDeletedCaption,
            }),
          });

          if (res.ok) {
            setPhotos(prev => prev.filter(p => p.id !== photo.id));
            if (selectedPhoto?.id === photo.id) {
              setSelectedPhoto(null);
            }
            notify.success('Photo removed (soft delete)');
          } else {
            notify.error('Failed to remove photo');
          }
        } catch {
          notify.error('Failed to remove photo');
        } finally {
          setDeletingPhotoId(null);
        }
      }
    });
  };

  // ── Save caption ──
  const handleSaveCaption = async (photoId: string, caption: string): Promise<boolean> => {
    const targetPhoto = photos.find(p => p.id === photoId);
    if (!canEditPhotoDetailsFor(targetPhoto)) {
      return false;
    }
    try {
      const photo = targetPhoto;
      const photoWorkItemId = photo?.work_item_id || workItemId;
      const res = await fetch(`/api/v1/work-items/media/upload`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_id: photoId, work_item_id: photoWorkItemId, caption }),
      });
      if (res.ok) {
        setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption } : p));
        if (selectedPhoto?.id === photoId) setSelectedPhoto({ ...selectedPhoto, caption });
        notify.success('Description saved');
        return true;
      }
      notify.error('Failed to save description');
      return false;
    } catch {
      notify.error('Failed to save description');
      return false;
    }
  };

  // ── Delete caption ──
  const handleDeleteCaption = async (photoId: string): Promise<boolean> => {
    const targetPhoto = photos.find(p => p.id === photoId);
    if (!canEditPhotoDetailsFor(targetPhoto)) {
      return false;
    }
    try {
      const photo = targetPhoto;
      const photoWorkItemId = photo?.work_item_id || workItemId;
      const res = await fetch(`/api/v1/work-items/media/upload`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_id: photoId, work_item_id: photoWorkItemId, caption: '' }),
      });
      if (res.ok) {
        setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, caption: null } : p));
        if (selectedPhoto?.id === photoId) setSelectedPhoto({ ...selectedPhoto, caption: null });
        notify.success('Description deleted');
        return true;
      }
      notify.error('Failed to delete description');
      return false;
    } catch {
      notify.error('Failed to delete description');
      return false;
    }
  };

  // ── Save taken_at (edit photo time) ──
  const handleTakenAtSave = async (photoId: string, takenAt: string): Promise<boolean> => {
    const targetPhoto = photos.find(p => p.id === photoId);
    if (!canEditPhotoDetailsFor(targetPhoto)) {
      return false;
    }
    try {
      const photo = targetPhoto;
      const photoWorkItemId = photo?.work_item_id || workItemId;
      const res = await fetch(`/api/v1/work-items/media/upload`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_id: photoId, work_item_id: photoWorkItemId, taken_at: takenAt }),
      });
      if (res.ok) {
        const data = await res.json();
        const updated = data.data || {};
        const newTakenAt = updated.captured_at || takenAt;
        const newUpdatedAt = updated.updated_at || new Date().toISOString();
        setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, taken_at: newTakenAt, updated_at: newUpdatedAt, is_edited: true, original_taken_at: p.original_taken_at || photo?.taken_at || null } : p));
        if (selectedPhoto?.id === photoId) setSelectedPhoto({ ...selectedPhoto, taken_at: newTakenAt, updated_at: newUpdatedAt, is_edited: true, original_taken_at: selectedPhoto.original_taken_at || photo?.taken_at || null });
        notify.success('Photo time updated');
        return true;
      }
      notify.error('Failed to update photo time');
      return false;
    } catch {
      notify.error('Failed to update photo time');
      return false;
    }
  };

  // ── Change visibility ──
  const handleVisibilityChange = async (photoId: string, visibility: PhotoVisibility): Promise<boolean> => {
    const targetPhoto = photos.find(p => p.id === photoId);
    if (!canEditPhotoDetailsFor(targetPhoto)) {
      notifyPhotoEditBlocked(true);
      return false;
    }
    try {
      const photo = targetPhoto;
      const photoWorkItemId = photo?.work_item_id || workItemId;
      const res = await fetch(`/api/v1/work-items/media/upload`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_id: photoId, work_item_id: photoWorkItemId, visibility }),
      });
      if (res.ok) {
        setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, visibility } : p));
        if (selectedPhoto?.id === photoId) setSelectedPhoto({ ...selectedPhoto, visibility });
        notify.success(`Photo visibility set to ${PHOTO_VISIBILITY_CONFIG[visibility].label}`);
        return true;
      }
      notify.error('Failed to update photo visibility');
      return false;
    } catch {
      notify.error('Failed to update photo visibility');
      return false;
    }
  };

  // ── Thumbnail visibility quick-toggle ──
  const handleThumbVisibilityChange = async (photo: Photo, visibility: PhotoVisibility) => {
    setThumbVisibilityMenuId(null);
    if (!canEditPhotoDetailsFor(photo)) {
      notifyPhotoEditBlocked(true);
      return;
    }
    setThumbVisibilitySavingId(photo.id);
    await handleVisibilityChange(photo.id, visibility);
    setThumbVisibilitySavingId(null);
  };

  // ── Derived data ──
  const filteredPhotos = filter === 'all' ? photos : photos.filter(p => p.type === filter);

  const countByType = (type: PhotoType) => photos.filter(p => p.type === type).length;

  // Only show filter buttons for types that have photos
  const activeTypes = ALL_PHOTO_TYPES.filter(t => countByType(t) > 0);

  const handlePhotoClick = (photo: Photo) => setSelectedPhoto(photo);

  const handleNext = () => {
    if (!selectedPhoto) return;
    const idx = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
    if (idx < filteredPhotos.length - 1) setSelectedPhoto(filteredPhotos[idx + 1]);
  };

  const handlePrevious = () => {
    if (!selectedPhoto) return;
    const idx = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
    if (idx > 0) setSelectedPhoto(filteredPhotos[idx - 1]);
  };

  // ════════════════════════════════════════════
  // LOADING STATE
  // ════════════════════════════════════════════

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <BrandedLoadingSpinner mode="compact" size="md" showSurface={false} showProgressBar={false} />
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>Loading photos...</p>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════
  // EMPTY STATE
  // ════════════════════════════════════════════

  if (photos.length === 0) {
    return (
      <div className="space-y-4">
        <AppointmentCompletionRequirementOverrideCard
          workItem={appointmentWorkItem}
          appointmentTypeOptions={appointmentTypeOptions}
          requirementKey="photos"
          title="Completion photos"
          description="Use this only when the appointment type allows a photo override and the field evidence genuinely cannot be collected."
          placeholder="Explain why completion photos can be bypassed for this appointment..."
          disabled={uploading || !canEditPhotos}
          onChange={onAppointmentRequirementOverrideChange || (() => { })}
        />

        <div
          onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
          className={`text-center py-12 border-2 border-dashed rounded-xl transition-colors ${dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
            }`}
        >
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileInput} className="hidden" />
          {uploading ? (
            <>
              <BrandedLoadingSpinner mode="compact" size="lg" showSurface={false} showProgressBar={false} />
              <p className="text-blue-600 dark:text-blue-400 font-medium mb-2">Uploading photos...</p>
            </>
          ) : (
            <>
              <Camera className={`w-16 h-16 mx-auto mb-3 ${dragActive ? 'text-blue-500' : isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg mb-2 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                {dragActive ? 'Drop photos here!' : 'No Photos Yet'}
              </p>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Drag and drop photos here, or click to upload
              </p>
              <div className="flex items-center justify-center gap-2 mb-4">
                <UploadTypeSelect value={uploadType} onChange={setUploadType} disabled={!canEditPhotos} isDark={isDark} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !canEditPhotos}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Upload className="w-4 h-4" /> Upload Photos
                </button>
              </div>
              <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Photos captured by field workers on mobile will also appear here
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════
  // MAIN RENDER — has photos
  // ════════════════════════════════════════════

  return (
    <div className="space-y-6" data-tour-id="work-item-photos-tab" onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
      <AppointmentCompletionRequirementOverrideCard
        workItem={appointmentWorkItem}
        appointmentTypeOptions={appointmentTypeOptions}
        requirementKey="photos"
        title="Completion photos"
        description="Use this only when the appointment type allows a photo override and the field evidence genuinely cannot be collected."
        placeholder="Explain why completion photos can be bypassed for this appointment..."
        disabled={uploading || !canEditPhotos}
        onChange={onAppointmentRequirementOverrideChange || (() => { })}
      />

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileInput} className="hidden" />

      {/* Drag overlay */}
      {dragActive && (
        <div className="fixed inset-0 bg-blue-500/20 z-40 flex items-center justify-center pointer-events-none">
          <div className={`rounded-xl p-8 shadow-2xl text-center ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <Upload className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <p className={`text-xl font-semibold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Drop photos here</p>
            <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>to upload as {PHOTO_TYPE_CONFIG[uploadType].label}</p>
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className={`rounded-lg p-4 flex items-center gap-3 ${isDark ? 'bg-blue-900/30 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <p className={isDark ? 'text-blue-300 font-medium' : 'text-blue-700 font-medium'}>Uploading photos...</p>
        </div>
      )}

      {/* Header with Stats & Upload */}
      <div className={`rounded-xl p-6 border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Camera className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>Field Service Photos</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {photos.length} photo{photos.length !== 1 ? 's' : ''} captured
              </p>
            </div>
            <UserDocsLink docsPath="/total-operations/workflows/service-operations/photos-documents#field-photos" label="User guide: Photos & documents" />
          </div>

          <div className="flex items-center gap-2">
            <UploadTypeSelect value={uploadType} onChange={setUploadType} disabled={!canEditPhotos} isDark={isDark} />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !canEditPhotos}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" /> Add Photos
            </button>
          </div>
        </div>

        {/* Filter Buttons — All + only types with photos */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${filter === 'all'
              ? 'bg-blue-600 text-white'
              : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
          >
            All ({photos.length})
          </button>
          {activeTypes.map(t => {
            const cfg = PHOTO_TYPE_CONFIG[t];
            const count = countByType(t);
            return (
              <button
                key={t}
                onClick={() => setFilter(t)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${filter === t
                  ? cfg.filterActive
                  : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
              >
                {cfg.emoji} {cfg.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredPhotos.map((photo) => {
          const cfg = getTypeConfig(photo.type);
          return (
            <div
              key={photo.id}
              onClick={() => handlePhotoClick(photo)}
              className={`group relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all hover:shadow-lg ${isDark ? 'border-gray-700 hover:border-blue-500 bg-gray-800' : 'border-gray-200 hover:border-blue-400 bg-gray-100'
                }`}
            >
              {/* Image + overlay badges (anchored to the image, not the card) */}
              <div className="relative">
                <img src={photo.url} alt={`${cfg.label} photo`} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />

                {/* Type Badge */}
                <div className={`absolute top-2 left-2 px-3 py-1 rounded-full text-xs font-bold border shadow-sm z-10 ${isDark ? cfg.badgeDark : cfg.badgeLight}`}>
                  {cfg.emoji} {cfg.label}
                </div>

                {/* Appointment origin badge — always show appointment number when available */}
                {photo.source_appointment_number ? (
                  <AppointmentReferenceBadge
                    appointmentId={photo.source_appointment_id}
                    appointmentNumber={photo.source_appointment_number}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-500/90 text-white z-10 backdrop-blur-sm hover:bg-indigo-500 transition-colors"
                    iconClassName="w-3 h-3"
                    title={`Open appointment ${photo.source_appointment_number}`}
                  >
                    <>
                      <span>📋 {photo.source_appointment_number}</span>
                    </>
                  </AppointmentReferenceBadge>
                ) : photo.is_from_related ? (
                  <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-500/80 text-white z-10">
                    Appointment
                  </div>
                ) : null}

                {/* Visibility badge — always shown; opens a quick-change menu while the item is editable */}
                <div className="absolute bottom-2 left-2 z-20 flex items-center gap-1">
                  {(() => {
                    const vCfg = getVisibilityConfig(photo.visibility);
                    const badgeClasses = `inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold backdrop-blur-sm shadow-sm border border-white/20 ${isDark ? vCfg.badgeDark : vCfg.badgeLight}`;
                    if (!canEditPhotoDetailsFor(photo)) {
                      return (
                        <div className={badgeClasses} title={`Visibility: ${vCfg.label}`}>
                          {React.createElement(vCfg.icon, { className: 'w-3 h-3' })}
                          {vCfg.label}
                        </div>
                      );
                    }
                    const menuOpen = thumbVisibilityMenuId === photo.id;
                    return (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setThumbVisibilityMenuId(menuOpen ? null : photo.id);
                          }}
                          disabled={thumbVisibilitySavingId === photo.id}
                          className={`${badgeClasses} hover:opacity-90 transition-opacity disabled:opacity-60`}
                          title={`Visibility: ${vCfg.label} — click to change`}
                        >
                          {thumbVisibilitySavingId === photo.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            React.createElement(vCfg.icon, { className: 'w-3 h-3' })
                          )}
                          {vCfg.label}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        {menuOpen && (
                          <>
                            <div
                              className="fixed inset-0 z-30"
                              onClick={(e) => {
                                e.stopPropagation();
                                setThumbVisibilityMenuId(null);
                              }}
                            />
                            <div className={`absolute left-0 bottom-full mb-1 z-40 rounded-lg shadow-xl border py-1 min-w-[150px] ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                              {ALL_PHOTO_VISIBILITY_VALUES.map((v) => {
                                const optCfg = PHOTO_VISIBILITY_CONFIG[v];
                                const isActive = (photo.visibility || 'customer') === v;
                                return (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void handleThumbVisibilityChange(photo, v);
                                    }}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-medium transition-colors text-left ${isActive
                                      ? isDark ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                                      : isDark ? 'text-gray-300 hover:bg-gray-700/50' : 'text-gray-700 hover:bg-gray-50'
                                      }`}
                                  >
                                    {React.createElement(optCfg.icon, { className: 'w-3.5 h-3.5' })}
                                    {optCfg.label}
                                    {isActive && <Check className="w-3.5 h-3.5 ml-auto text-green-500" />}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* AI flag/verified badge */}
                  {photo.ai_analysis?.flagged && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-600/90 text-white text-[10px] font-bold backdrop-blur-sm" title={photo.ai_analysis.flag_reason || 'AI flagged'}>
                      <AlertTriangle className="w-3 h-3" /> Flagged
                    </div>
                  )}
                  {photo.ai_analysis && !photo.ai_analysis.flagged && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-600/90 text-white text-[10px] font-bold backdrop-blur-sm">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </div>
                  )}
                </div>

                {/* Zoom overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-black/30 transition-all pointer-events-none">
                  <ZoomIn className="w-8 h-8 text-white" />
                </div>

                {/* Delete button */}
                {canEditPhotoDetailsFor(photo) ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePhoto(photo);
                    }}
                    disabled={deletingPhotoId === photo.id}
                    className="absolute bottom-2 right-2 p-1.5 rounded-full bg-red-600/90 hover:bg-red-700 text-white z-20 shadow-md opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-60"
                    title="Soft delete photo"
                  >
                    {deletingPhotoId === photo.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                ) : null}
              </div>

              {/* Info */}
              <div className={`p-3 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                {photo.ai_analysis?.flagged && (
                  <div className="flex items-start gap-1.5 mb-2 px-2 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-[11px] text-red-600 dark:text-red-400 font-medium line-clamp-2">{photo.ai_analysis.flag_reason || 'This photo may not match the job context'}</span>
                  </div>
                )}
                {photo.caption && (
                  <p className={`text-xs mb-2 line-clamp-2 italic ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>&quot;{photo.caption}&quot;</p>
                )}
                <div className={`flex items-center justify-between text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {photo.taken_by && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span className="truncate">{photo.taken_by}</span>
                    </div>
                  )}
                  {photo.location && <MapPin className="w-3 h-3 text-green-500" />}
                </div>
                {photo.taken_at && (
                  <div className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    <Clock className="w-3 h-3" />
                    <span>{formatDateTime(photo.taken_at)}</span>
                    {photo.is_edited && (
                      <span className="text-[10px] italic text-gray-500">(edited)</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <Lightbox
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          onNext={filteredPhotos.indexOf(selectedPhoto) < filteredPhotos.length - 1 ? handleNext : undefined}
          onPrevious={filteredPhotos.indexOf(selectedPhoto) > 0 ? handlePrevious : undefined}
          onCaptionSave={canEditPhotoDetailsFor(selectedPhoto) ? handleSaveCaption : undefined}
          onCaptionDelete={canEditPhotoDetailsFor(selectedPhoto) ? handleDeleteCaption : undefined}
          onVisibilityChange={canEditPhotoDetailsFor(selectedPhoto) ? handleVisibilityChange : undefined}
          onTakenAtSave={canEditPhotoDetailsFor(selectedPhoto) ? handleTakenAtSave : undefined}
        />
      )}
    </div>
  );
}
